import ax from 'axios';

const sessionLifetime = getSessionLifetime();
const SESSION_REFRESH_INTERVAL = Number.isFinite(sessionLifetime) && sessionLifetime > 0
	? Math.min(5 * 60 * 1000, sessionLifetime * 60 * 1000 / 2)
	: 5 * 60 * 1000;
const SESSION_REFRESHED_AT_KEY = 'session-refreshed-at';
const SESSION_REFRESH_LOCK = 'session-refresh';
let hasAuthenticatedSession = document.head.querySelector('meta[name="session-authenticated"]')?.content === '1';
// The page load that delivered this script also issued fresh session and API token
// cookies, so the session is treated as refreshed from the moment the module loads.
let lastSessionRefreshAt = Date.now();
let sessionRefreshPromise = null;

if (hasAuthenticatedSession) {
	setLastSessionRefreshAt(lastSessionRefreshAt);
}

export function getSessionLifetime() {
	return Number(document.head.querySelector('meta[name="session-lifetime"]')?.content);
}

export function getLastSessionRefreshAt() {
	try {
		return Math.max(
			lastSessionRefreshAt,
			Number(localStorage.getItem(SESSION_REFRESHED_AT_KEY)) || 0,
		);
	} catch {
		return lastSessionRefreshAt;
	}
}

function setLastSessionRefreshAt(timestamp) {
	lastSessionRefreshAt = timestamp;

	try {
		localStorage.setItem(SESSION_REFRESHED_AT_KEY, timestamp);
	} catch {
		// The in-memory request lock still prevents duplicate refreshes in this tab.
	}
}

function isApiRequest(config) {
	return config?.url?.startsWith('/api/') && !config.skipSessionRefresh;
}

function sessionRefreshIsDue() {
	return Date.now() - getLastSessionRefreshAt() >= SESSION_REFRESH_INTERVAL;
}

async function refreshSession() {
	const performRefresh = async () => {
		if (!sessionRefreshIsDue()) {
			return;
		}

		const response = await ax.get('/session/refresh', {
			skipSessionRefresh: true,
			headers: {Accept: 'application/json'},
		});
		if (response.status === 204) {
			setLastSessionRefreshAt(Date.now());
		}
	};

	if (globalThis.navigator?.locks) {
		await globalThis.navigator.locks.request(SESSION_REFRESH_LOCK, performRefresh);

		return;
	}

	await performRefresh();
}

function refreshSessionAfterApiActivity(config) {
	if (!hasAuthenticatedSession || !isApiRequest(config) || !sessionRefreshIsDue()) {
		return;
	}

	if (!sessionRefreshPromise) {
		sessionRefreshPromise = refreshSession()
			.catch(() => {})
			.finally(() => {
				sessionRefreshPromise = null;
			});
	}
}

function redirectAndHold(target) {
	window.location = target;

	// Never settle so callers do not flash error handling while the browser navigates away.
	return new Promise(() => {});
}

function handleSuccessfulResponse(response) {
	if (response.config.url === '/api/user') {
		hasAuthenticatedSession = true;
	}

	refreshSessionAfterApiActivity(response.config);

	return response;
}

ax.interceptors.response.use(
	handleSuccessfulResponse,
	function (error) {
		if (ax.isCancel(error)) {
			return Promise.reject(error);
		}

		if (error.response?.status === 401) {
			hasAuthenticatedSession = false;

			return redirectAndHold('/login');
		}

		if (error.response?.status === 503) {
			return Promise.reject(error);
		}

		refreshSessionAfterApiActivity(error.config);

		return Promise.reject(error);
	}
);

// Axios reads XSRF-TOKEN for each request, including cookies renewed by other tabs.
ax.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
ax.defaults.withCredentials = true;

export const axios = ax;
export default {
	install(app) {
		app.config.globalProperties.$axios = ax;
	},
};
